/**
 * Mobile QR scanner entry for valve-body sale / install registration.
 * Mirrors the native Android header QrCodeScanner → parse tag → RegisterValveBody flow.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAuth } from "context/AuthContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import { IconQrScanner } from "mobile/components/MobileNavIcons";
import MxShell from "mobile/mx/components/MxShell";
import { mx } from "mobile/mx/lib/theme";
import VbsShell from "mobile/vbsus/components/VbsShell";
import { vbs } from "mobile/vbsus/lib/theme";

function parseTagFromScannedValue(raw) {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed, window.location.origin);
    const segments = parsed.pathname.split("/").filter(Boolean);
    const idx = segments.findIndex((part) => part.toLowerCase() === "valve-body");
    if (idx >= 0 && segments[idx + 1]) {
      const value = decodeURIComponent(segments[idx + 1]).trim();
      if (value) return value;
    }
    const queryTag = ["tag", "identifier"]
      .map((key) => parsed.searchParams.get(key)?.trim())
      .find(Boolean);
    if (queryTag) return queryTag;
  } catch {
    /* not a URL */
  }

  const pathMatch = trimmed.match(/valve-body\/([^/?#]+)/i);
  if (pathMatch?.[1]) {
    try {
      return decodeURIComponent(pathMatch[1]).trim() || null;
    } catch {
      return pathMatch[1].trim() || null;
    }
  }

  return trimmed;
}

function supportsBarcodeDetector() {
  return typeof window !== "undefined" && typeof window.BarcodeDetector === "function";
}

export default function MobileQrScan() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { tenantKey } = useStorefrontTenant();
  const { t } = useStorefrontCopy();
  const theme = tenantKey === "mx" ? mx : vbs;
  const Shell = tenantKey === "mx" ? MxShell : VbsShell;
  // Account hub entry or any existing JWT — register flow skips login/signup.
  const fromAccount =
    String(searchParams.get("from") || "").trim() === "account" || Boolean(token);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(0);
  const handledRef = useRef(false);

  const [cameraError, setCameraError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [manualTag, setManualTag] = useState("");

  const stopCamera = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setScanning(false);
  }, []);

  const openRegistration = useCallback(
    (raw) => {
      const tag = parseTagFromScannedValue(raw);
      if (!tag || handledRef.current) return;
      handledRef.current = true;
      stopCamera();
      const params = new URLSearchParams({ qr: "1", web: "1" });
      if (fromAccount) params.set("from", "account");
      navigate(`/register/valve-body/${encodeURIComponent(tag)}?${params.toString()}`);
    },
    [fromAccount, navigate, stopCamera],
  );

  const startCamera = useCallback(async () => {
    handledRef.current = false;
    setCameraError("");
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraError(
        t(
          "registeredUnits.cameraUnsupported",
          "Camera scanning is not available in this browser. Enter the tag below.",
        ),
      );
      return;
    }

    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      video.srcObject = stream;
      await video.play();
      setScanning(true);

      if (!supportsBarcodeDetector()) {
        setCameraError(
          t(
            "registeredUnits.detectorUnsupported",
            "Live QR decode is limited on this browser. Point your camera, or enter the tag below.",
          ),
        );
        return;
      }

      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      const tick = async () => {
        if (!videoRef.current || videoRef.current.readyState < 2 || handledRef.current) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        try {
          const codes = await detector.detect(videoRef.current);
          const raw = codes?.[0]?.rawValue;
          if (raw) {
            openRegistration(raw);
            return;
          }
        } catch {
          /* keep scanning */
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      setCameraError(
        t(
          "registeredUnits.cameraPermission",
          "Camera permission is needed to scan QR codes. You can enable it in browser settings, or enter the tag below.",
        ),
      );
      setScanning(false);
    }
  }, [openRegistration, stopCamera, t]);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  const onManualSubmit = (event) => {
    event.preventDefault();
    openRegistration(manualTag);
  };

  return (
    <Shell
      title={t("registeredUnits.scanTitle", "Scan QR")}
      onBack={() => navigate(-1)}
    >
      <Box sx={{ px: 1.5, pb: 10, pt: 0.5, bgcolor: theme.canvas, minHeight: "70vh" }}>
        <Stack spacing={1.25}>
          <Box
            sx={{
              bgcolor: theme.surface,
              border: `1px solid ${theme.hairline}`,
              borderRadius: "14px",
              p: 1.5,
              boxShadow: "0 1px 2px rgba(15,15,18,0.04)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ color: theme.accent, display: "flex" }}>
                <IconQrScanner fontSize={22} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 15.5, color: theme.ink, letterSpacing: "-0.02em" }}>
                  {t("registeredUnits.scanHeading", "Register a valve body")}
                </Typography>
                <Typography sx={{ fontSize: 13, color: theme.muted, mt: 0.15 }}>
                  {t(
                    "registeredUnits.scanHint",
                    "Scan the unit QR to confirm a sale or register an install.",
                  )}
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                position: "relative",
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: "#0F0F12",
                aspectRatio: "3 / 4",
                maxHeight: "min(58dvh, 420px)",
              }}
            >
              <Box
                component="video"
                ref={videoRef}
                muted
                playsInline
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  opacity: scanning ? 1 : 0.35,
                }}
              />
              <Box
                sx={{
                  pointerEvents: "none",
                  position: "absolute",
                  inset: "18%",
                  border: "2px solid rgba(255,92,0,0.85)",
                  borderRadius: "12px",
                  boxShadow: "0 0 0 999px rgba(15,15,18,0.28)",
                }}
              />
            </Box>

            {cameraError ? (
              <Typography sx={{ mt: 1, fontSize: 12.5, color: theme.muted, lineHeight: 1.4 }}>
                {cameraError}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
              <ButtonBase
                type="button"
                onClick={() => startCamera()}
                sx={{
                  flex: 1,
                  minHeight: 44,
                  borderRadius: "12px",
                  bgcolor: theme.accentSoft,
                  color: theme.accentHover,
                  fontWeight: 750,
                  fontSize: 13.5,
                }}
              >
                {t("registeredUnits.rescan", "Restart camera")}
              </ButtonBase>
            </Stack>
          </Box>

          <Box
            component="form"
            onSubmit={onManualSubmit}
            sx={{
              bgcolor: theme.surface,
              border: `1px solid ${theme.hairline}`,
              borderRadius: "14px",
              p: 1.5,
              boxShadow: "0 1px 2px rgba(15,15,18,0.04)",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 750,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: theme.subtle,
                mb: 0.75,
              }}
            >
              {t("registeredUnits.manualTag", "Or enter tag")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={manualTag}
              onChange={(event) => setManualTag(event.target.value)}
              placeholder={t("registeredUnits.tagPlaceholder", "Tag / serial")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: theme.canvas,
                },
              }}
            />
            <ButtonBase
              type="submit"
              disabled={!manualTag.trim()}
              sx={{
                mt: 1,
                width: "100%",
                minHeight: 46,
                borderRadius: "12px",
                bgcolor: theme.accent,
                color: "#fff",
                fontWeight: 800,
                fontSize: 14.5,
                opacity: manualTag.trim() ? 1 : 0.45,
              }}
            >
              {t("registeredUnits.continueTag", "Continue")}
            </ButtonBase>
          </Box>
        </Stack>
      </Box>
    </Shell>
  );
}
